import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import CloseIcon from '@mui/icons-material/Close';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DateRangeStyled, pillTabButtonSx } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fDate } from '../../utils/formatTime';
import Loader from '../Global/Loader';
import { setApiError, setApiSuccess } from '../../store/Slice/Error/ErrorSlice';

import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { getBankBranchData } from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import RevenueService from '../services/RevenueService';

const { DownloadMisDetails, UpdateStatus } = RevenueService;

export default function RevenueToolbar({
  filterName,
  onFilterName,
  parent = 'revenue',
  activeTab = 'receipt',
  onTabChange,
  fileStatusOptions = [],
  selected = [],
  filterDatesRange,
  setFilterDatesRange,
  setFilterParameter,
  filterParameter,
  masterTypeOptions
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [downloadPopup, setDownloadPopup] = useState(false);
  const [sendInvoice, setSendInvoice] = useState(false);
  const [openLoader, setOpenLoader] = useState(false);

  const { LoanTypeDrop, BankBranchDrop, BankDrop } = useGlobalSelector();
  const { LoginData } = useLoginSelector();

  const handleRemoveFilter = () => {
    setFilterDatesRange([
      {
        startDate: null,
        endDate: null,
        key: 'selection'
      }
    ]);

    setFilterParameter({
      loanTypeId: null,
      bankId: null,
      branchId: null,
      misIsMatch: null,
      misIsMatchId: null
    });
  };

  const handleDownloadMISDetails = async () => {
    const bankIds = selected?.map((item) => item?.bankId);
    const revenueIds = selected?.map((item) => item?.revenueId);
    setDownloadPopup(true);
    const credential = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      fileFormat: 'csv',
      revenueIds: revenueIds,
      bankIds: bankIds
    };
    try {
      const DownloadMisDetailsResponse = await DownloadMisDetails(credential, LoginData?.jwt);
      const blob = await DownloadMisDetailsResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'revenue-mis-file-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('error', error);
    } finally {
      setDownloadPopup(false);
    }
  };

  const handleSendInvoice = async () => {
    const authToken = LoginData?.jwt;
    const credentials = selected.map((item) => ({
      fileNumber: item?.fileNumber || '',
      bankId: item?.bankId || ''
    }));

    try {
      setOpenLoader(true);
      const SendInvoiceResponse = await UpdateStatus(credentials, authToken);

      if (SendInvoiceResponse.status === '0') {
        dispatch(setApiSuccess('Invoice sent successfully!'));
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else if (SendInvoiceResponse.status === '1') {
        dispatch(setApiError(SendInvoiceResponse.msg));
      } else {
        dispatch(setApiError(SendInvoiceResponse.msg));
      }
    } catch (error) {
      dispatch(setApiError(error?.message || 'Something went wrong'));
    } finally {
      setSendInvoice(false);
      setOpenLoader(false);
    }
  };

  const handleAddClick = () => {
    const tabName = (activeTab?.label || activeTab || '').toLowerCase();
    if (tabName.includes('approved')) {
      navigate('/revenue/add-approved');
    } else if (tabName.includes('disbursed')) {
      navigate('/revenue/add-disbursed');
    } else {
      navigate('/revenue/add-receipt');
    }
  };

  const handleUploadClick = () => {
    const tabName = (activeTab?.label || activeTab || '').toLowerCase();
    if (tabName.includes('approved')) {
      navigate('/revenue/upload-approved');
    } else if (tabName.includes('disbursed')) {
      navigate('/revenue/upload-disbursed');
    } else {
      navigate('/revenue/upload-receipt');
    }
  };

  const tabList = [
    {
      id: 'disbursed',
      label: 'Disbursed',
      icon: <CurrencyRupeeIcon sx={{ fontSize: 18 }} />
    }
  ];

  return (
    <>
      {/* Top Toolbar: Left Tabs & Right Action Tools */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        {/* Left Side: 3 Styled Tab Buttons */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{ overflowX: 'auto', p: '2px 2px 6px' }}
        >
          {tabList.map((tab) => {
            const isTabActive =
              (typeof activeTab === 'string' && activeTab.toLowerCase().includes(tab.id)) ||
              (activeTab?.label && activeTab.label.toLowerCase().includes(tab.id)) ||
              (activeTab?.value &&
                fileStatusOptions
                  ?.find((o) => o.value === activeTab?.value)
                  ?.label?.toLowerCase()
                  .includes(tab.id));

            return (
              <Button
                key={tab.id}
                onClick={() => {
                  const matchingOption = fileStatusOptions.find((o) =>
                    o.label.toLowerCase().includes(tab.id)
                  );
                  if (onTabChange && matchingOption) {
                    onTabChange(matchingOption.value, matchingOption.label);
                  }
                }}
                startIcon={tab.icon}
                sx={pillTabButtonSx(isTabActive)}
              >
                {tab.label}
              </Button>
            );
          })}
        </Stack>

        {/* Right Side: + Add, Upload, Download, Search, Filter */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {/* Blue + Add Button */}
          <Tooltip title="Add New MIS Entry" arrow>
            <Button
              variant="contained"
              onClick={handleAddClick}
              sx={{
                minWidth: 40,
                width: 40,
                height: 40,
                p: 0,
                bgcolor: '#0284c7',
                color: '#ffffff',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              <AddIcon sx={{ fontSize: 22 }} />
            </Button>
          </Tooltip>

          {/* Upload Button */}
          <Tooltip title="Upload MIS File" arrow>
            <IconButton
              onClick={handleUploadClick}
              sx={{
                width: 40,
                height: 40,
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                bgcolor: '#ffffff',
                color: '#0284c7',
                '&:hover': {
                  borderColor: '#0284c7',
                  bgcolor: 'rgba(2, 132, 199, 0.06)'
                }
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {/* Download Button */}
          <Tooltip title="Download MIS Template / Report" arrow>
            <span>
              <IconButton
                // disabled={!selected?.length > 0}
                onClick={() => setDownloadPopup(true)}
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid #bbf7d0',
                  borderRadius: '10px',
                  bgcolor: '#f0fdf4',
                  color: '#15803d',
                  '&:hover': {
                    borderColor: '#16a34a',
                    bgcolor: '#dcfce7'
                  }
                }}
              >
                <CloudDownloadOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>

          {(activeTab?.label || activeTab || '').toLowerCase() === 'disbursed' && (
            <Tooltip title="Send Invoice" arrow>
              <span>
                <IconButton
                  disabled={selected?.length < 1}
                  onClick={() => setSendInvoice(true)}
                  sx={{
                    width: 40,
                    height: 40,
                    border: '1px solid #e2e8f0',
                    borderRadius: '10px',
                    bgcolor: '#ffffff',
                    color: '#0284c7',
                    '&:hover': {
                      borderColor: '#0284c7',
                      bgcolor: 'rgba(2, 132, 199, 0.06)'
                    }
                  }}
                >
                  <SendOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Search Field */}
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Revenue..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 240, md: 270 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }
            }}
          />

          {/* Filter Toggle Button */}
          <Tooltip title={isOpenFilter ? 'Hide Filters' : 'Show Filters'} arrow>
            <IconButton
              onClick={() => setOpenFilter((prev) => !prev)}
              sx={{
                width: 40,
                height: 40,
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.08)' : '#ffffff',
                color: '#0284c7',
                '&:hover': {
                  borderColor: '#0284c7',
                  bgcolor: 'rgba(2, 132, 199, 0.08)'
                }
              }}
            >
              <FilterAltOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Expandable Filter Drawer / Panel */}
      {isOpenFilter && (
        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Grid container spacing={2} alignItems="center">
            {/* Bank Autocomplete */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                id="combo-box-bank"
                options={BankDrop || []}
                size="small"
                getOptionLabel={(option) => option.label || ''}
                renderOption={(props, option) => <li {...props}>{option.label}</li>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select Bank"
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                    }}
                  />
                )}
                value={BankDrop?.find((item) => item.value === filterParameter?.bankId) || null}
                onChange={(e, newValue) => {
                  setFilterParameter((prev) => ({
                    ...prev,
                    bankId: newValue?.value,
                    branchId: null
                  }));
                  if (newValue?.value) {
                    dispatch(getBankBranchData(newValue?.value));
                  }
                }}
              />
            </Grid>

            {/* Branch Autocomplete */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                id="combo-box-branch"
                disabled={!filterParameter?.bankId}
                options={BankBranchDrop || []}
                size="small"
                getOptionLabel={(option) => option.label || ''}
                renderOption={(props, option) => <li {...props}>{option.label}</li>}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select Branch"
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                    }}
                  />
                )}
                value={
                  BankBranchDrop?.find((item) => item.value === filterParameter?.branchId) || null
                }
                onChange={(e, newValue) => {
                  setFilterParameter((prev) => ({
                    ...prev,
                    branchId: newValue?.value
                  }));
                }}
              />
            </Grid>

            {/* Loan Product Type */}
            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={filterParameter?.loanTypeId || ' '}
                onChange={(e) => {
                  setFilterParameter((prev) => ({
                    ...prev,
                    loanTypeId: e.target?.value === ' ' ? null : e.target?.value
                  }));
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  fontSize: '13px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " disabled sx={{ fontSize: 13 }}>
                  Select Loan Type
                </MenuItem>
                {LoanTypeDrop?.map((item) => (
                  <MenuItem value={item.value} key={item.value} sx={{ fontSize: 13 }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {filterDatesRange[0].startDate !== null
                  ? `${fDate(filterDatesRange[0].startDate)} to `
                  : ''}
                {filterDatesRange[0].endDate !== null ? fDate(filterDatesRange[0].endDate) : ''}
                {filterDatesRange[0].startDate === null && filterDatesRange[0].endDate === null ? (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                ) : (
                  ''
                )}
              </Box>
              <Select
                fullWidth
                inputProps={{
                  className: 'textInput'
                }}
                MenuProps={{
                  className: 'menuOpened'
                }}
                value=" "
                className="datesSelect"
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) => setFilterDatesRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>

            {/* MIS Status */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Select
                fullWidth
                size="small"
                value={filterParameter?.misIsMatchId || ' '}
                onChange={(e) => {
                  setFilterParameter((prev) => ({
                    ...prev,
                    misIsMatch:
                      e.target?.value === ' '
                        ? null
                        : masterTypeOptions?.statusOptions?.find(
                            (item) => item.value === e.target?.value
                          )?.label,
                    misIsMatchId: e.target?.value === ' ' ? null : e.target?.value
                  }));
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#ffffff',
                  fontSize: '13px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " disabled sx={{ fontSize: 13 }}>
                  Select MIS Status
                </MenuItem>
                {masterTypeOptions?.statusOptions?.map((item) => (
                  <MenuItem value={item?.value} key={item?.value} sx={{ fontSize: 13 }}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={6} md={0.5}>
              <Tooltip title="Reset Filters" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(239, 68, 68, 0.08)',
                    color: '#ef4444',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.16)' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Download Confirmation Dialog */}
      <Dialog
        open={downloadPopup}
        onClose={() => setDownloadPopup(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>
            Download MIS Details
          </Typography>
          <IconButton
            onClick={() => setDownloadPopup(false)}
            size="small"
            sx={{ color: '#64748b' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#475569', fontSize: '14px', lineHeight: 1.5 }}>
            Are you sure you want to download the MIS records in CSV format?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDownloadPopup(false)}
            sx={{
              borderRadius: '8px',
              borderColor: '#cbd5e1',
              color: '#64748b',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDownloadMISDetails}
            sx={{
              borderRadius: '8px',
              bgcolor: '#0284c7',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Confirm Download
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendInvoice}
        onClose={() => setSendInvoice(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '16px' }}>
            Send Invoice
          </Typography>
          <IconButton onClick={() => setSendInvoice(false)} size="small" sx={{ color: '#64748b' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#475569', fontSize: '14px', lineHeight: 1.5 }}>
            Are you sure you want to send this invoice ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleSendInvoice}
            sx={{
              borderRadius: '8px',
              bgcolor: '#0284c7',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Loader openLoader={openLoader} />
    </>
  );
}
